<template>
  <div class="landing-root" :class="{ 'light-mode': !darkMode, 'dark-mode': darkMode }">

    <!-- Fixed nav -->
    <nav class="ps-nav" :class="{ scrolled: scrolled }">
      <div class="ps-nav-inner">
        <a href="javascript:void(0)" @click="scrollTo('top')" class="ps-brand">
          <img src="/assets/images/paysol_logo.png" alt="Paysol" />
        </a>
        <div class="ps-nav-links">
          <a href="#features"      @click.prevent="scrollTo('features')">{{ t('landing.nav.features') }}</a>
          <a href="#verticals"     @click.prevent="scrollTo('verticals')">{{ t('landing.nav.verticals') }}</a>
          <a href="#how"           @click.prevent="scrollTo('how')">{{ t('landing.nav.howItWorks') }}</a>
          <a href="#oversight"     @click.prevent="scrollTo('oversight')">{{ t('landing.nav.oversight') }}</a>
          <a href="#pricing"       @click.prevent="scrollTo('pricing')">{{ t('landing.nav.pricing') }}</a>
        </div>
        <div class="ps-nav-cta">
          <LanguageToggle />
          <button class="ps-theme-toggle" @click="toggleTheme" :title="darkMode ? 'Switch to Light' : 'Switch to Dark'">
            <i :class="darkMode ? 'mdi mdi-white-balance-sunny' : 'mdi mdi-moon-waning-crescent'"></i>
          </button>
          <router-link to="/login"    class="ps-btn ps-btn-ghost">{{ t('common.signIn') }}</router-link>
          <router-link to="/register" class="ps-btn ps-btn-primary">{{ t('common.getStarted') }}</router-link>
        </div>
        <button class="ps-nav-burger" @click="mobileOpen = !mobileOpen">
          <i :class="mobileOpen ? 'mdi mdi-close' : 'mdi mdi-menu'"></i>
        </button>
      </div>
      <div v-if="mobileOpen" class="ps-nav-mobile">
        <a href="#features"  @click.prevent="scrollTo('features'); mobileOpen=false">Features</a>
        <a href="#verticals" @click.prevent="scrollTo('verticals'); mobileOpen=false">Verticals</a>
        <a href="#how"       @click.prevent="scrollTo('how'); mobileOpen=false">How it works</a>
        <a href="#oversight" @click.prevent="scrollTo('oversight'); mobileOpen=false">Oversight</a>
        <a href="#pricing"   @click.prevent="scrollTo('pricing'); mobileOpen=false">Pricing</a>
        <div class="ps-nav-mobile-cta">
          <button class="ps-theme-toggle" @click="toggleTheme">
            <i :class="darkMode ? 'mdi mdi-white-balance-sunny' : 'mdi mdi-moon-waning-crescent'"></i>
            {{ darkMode ? 'Light mode' : 'Dark mode' }}
          </button>
          <router-link to="/login" class="ps-btn ps-btn-ghost full">Sign in</router-link>
          <router-link to="/register" class="ps-btn ps-btn-primary full">Get started free</router-link>
        </div>
      </div>
    </nav>

    <!-- HERO -->
    <section class="ps-hero" id="top">
      <div class="ps-hero-glow"></div>
      <div class="ps-container ps-hero-grid">
        <div class="ps-hero-copy">
          <div class="ps-badge">
            <i class="mdi mdi-shield-check-outline"></i>
            {{ t('landing.hero.badge') }}
          </div>
          <h1 class="ps-h1">
            {{ t('landing.hero.title') }}<br />
            {{ t('landing.hero.titleWith') }} <span class="ps-accent-word">{{ t('landing.hero.titleAccent') }}</span>.
          </h1>
          <p class="ps-hero-sub">{{ t('landing.hero.sub') }}</p>
          <div class="ps-hero-cta">
            <router-link to="/register" class="ps-btn ps-btn-primary ps-btn-lg">
              <i class="mdi mdi-rocket-launch-outline"></i>
              {{ t('landing.hero.ctaPrimary') }}
            </router-link>
            <a href="#how" @click.prevent="scrollTo('how')" class="ps-btn ps-btn-ghost ps-btn-lg">
              <i class="mdi mdi-play-circle-outline"></i>
              {{ t('landing.hero.ctaSecondary') }}
            </a>
          </div>
          <div class="ps-hero-trust">
            <div class="ps-trust-item">
              <i class="mdi mdi-store-outline"></i>
              <div><strong>{{ t('landing.hero.trustAnyVertical') }}</strong><span>{{ t('landing.hero.trustAnyVerticalSub') }}</span></div>
            </div>
            <div class="ps-trust-item">
              <i class="mdi mdi-cellphone-wireless"></i>
              <div><strong>{{ t('landing.hero.trustMpesa') }}</strong><span>{{ t('landing.hero.trustMpesaSub') }}</span></div>
            </div>
            <div class="ps-trust-item">
              <i class="mdi mdi-shield-lock-outline"></i>
              <div><strong>{{ t('landing.hero.trustAudit') }}</strong><span>{{ t('landing.hero.trustAuditSub') }}</span></div>
            </div>
            <div class="ps-trust-item">
              <i class="mdi mdi-cash-multiple"></i>
              <div><strong>{{ t('landing.hero.trustCash') }}</strong><span>{{ t('landing.hero.trustCashSub') }}</span></div>
            </div>
          </div>
        </div>

        <!-- Hero mockup: mini POS terminal -->
        <div class="ps-hero-mockup">
          <div class="ps-mock-frame">
            <div class="ps-mock-topbar">
              <div class="ps-mock-dot" style="background:#FF605C"></div>
              <div class="ps-mock-dot" style="background:#FFBD44"></div>
              <div class="ps-mock-dot" style="background:#00CA4E"></div>
              <div class="ps-mock-title">Paysol POS · Main Shop</div>
            </div>
            <div class="ps-mock-body">
              <div class="ps-mock-cart">
                <div class="ps-mock-cart-row" v-for="(it, i) in mockCart" :key="i">
                  <div>
                    <div class="ps-mock-item">{{ it.name }}</div>
                    <div class="ps-mock-var">{{ it.variant }}</div>
                  </div>
                  <div class="ps-mock-qty">×{{ it.qty }}</div>
                  <div class="ps-mock-price">KES {{ format(it.qty * it.price) }}</div>
                </div>
                <div class="ps-mock-total">
                  <span>Total</span>
                  <strong>KES {{ format(mockTotal) }}</strong>
                </div>
              </div>
              <div class="ps-mock-pay">
                <div class="ps-mock-pay-methods">
                  <div class="ps-mock-method">Cash</div>
                  <div class="ps-mock-method active">M-Pesa STK</div>
                  <div class="ps-mock-method">Card</div>
                </div>
                <button class="ps-mock-btn">
                  <i class="mdi mdi-check-circle-outline"></i>
                  Complete sale — KES {{ format(mockTotal) }}
                </button>
                <div class="ps-mock-receipt">
                  <i class="mdi mdi-receipt-text-outline"></i>
                  RCT-20260806-A4F2 · Auto SMS to 07** *** 921
                </div>
              </div>
            </div>
          </div>
          <div class="ps-mock-badge">
            <i class="mdi mdi-flash"></i>
            &lt; 3 s per sale
          </div>
        </div>
      </div>
    </section>

    <!-- VERTICALS -->
    <section id="verticals" class="ps-section">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">{{ t('landing.verticals.kicker') }}</div>
          <h2 class="ps-h2">{{ t('landing.verticals.title') }}</h2>
          <p class="ps-lead">{{ t('landing.verticals.lead') }}</p>
        </div>
        <div class="ps-verticals-grid">
          <div class="ps-vertical-card" v-for="v in verticals" :key="v.key">
            <div class="ps-vertical-ico" :style="{ background: v.tint }">
              <i :class="v.icon"></i>
            </div>
            <h4>{{ v.name }}</h4>
            <p>{{ v.desc }}</p>
            <div class="ps-vertical-tags">
              <span v-for="(t, ti) in v.tags" :key="ti">{{ t }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- OVERSIGHT — the differentiator -->
    <section id="oversight" class="ps-section ps-section-alt">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">The real spine</div>
          <h2 class="ps-h2">Trust, without the hovering.</h2>
          <p class="ps-lead">
            Every POS looks the same on the surface. What makes Paysol different is what happens
            <em>underneath</em> — every sensitive action is recorded, every cashier's till is
            reconciled, and only exceptions reach your phone. Leave the shop with confidence.
          </p>
        </div>
        <div class="ps-oversight-grid">
          <div class="ps-oversight-card" v-for="p in patterns" :key="p.title">
            <div class="ps-oversight-icon"><i :class="p.icon"></i></div>
            <h5>{{ p.title }}</h5>
            <p>{{ p.copy }}</p>
            <div class="ps-oversight-caught">
              <span class="ps-oversight-dot"></span>
              <em>Caught by:</em> {{ p.caught }}
            </div>
          </div>
        </div>
        <div class="ps-mental-model">
          <div class="ps-mental-inner">
            <div class="ps-mental-label">The mental model</div>
            <p>
              Staff operate. <strong>Paysol watches.</strong> You review only the exceptions —
              not every transaction. That is what lets you be absent without the business bleeding.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section id="features" class="ps-section">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">Everything in one place</div>
          <h2 class="ps-h2">Ten modules. One login. One truth.</h2>
          <p class="ps-lead">
            Every business needs stock, sales, cash accountability and reports.
            Paysol builds them all into a single system — no more separate spreadsheets, apps and receipts to reconcile.
          </p>
        </div>
        <div class="ps-features-grid">
          <div v-for="f in features" :key="f.title" class="ps-feature-card">
            <div class="ps-feature-icon"><i :class="f.icon"></i></div>
            <h4>{{ f.title }}</h4>
            <p>{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section id="how" class="ps-section ps-section-alt">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">Live in an afternoon</div>
          <h2 class="ps-h2">From zero to your first sale in four steps.</h2>
        </div>
        <div class="ps-steps-grid">
          <div v-for="(s, i) in steps" :key="i" class="ps-step-card">
            <div class="ps-step-num">{{ i + 1 }}</div>
            <div class="ps-step-icon"><i :class="s.icon"></i></div>
            <h5>{{ s.title }}</h5>
            <p>{{ s.copy }}</p>
          </div>
        </div>
        <div class="ps-cta-inline">
          <router-link to="/register" class="ps-btn ps-btn-primary ps-btn-lg">
            <i class="mdi mdi-arrow-right-circle"></i>
            Set up your business — free
          </router-link>
        </div>
      </div>
    </section>

    <!-- LIVE PREVIEW — sample WhatsApp end-of-day + owner dashboard -->
    <section class="ps-section">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">Owner-first design</div>
          <h2 class="ps-h2">What lands on your phone at close of business.</h2>
          <p class="ps-lead">
            No spreadsheets to open, no reports to run. Paysol pushes a clean daily digest to your WhatsApp
            or email — exceptions surface, the routine stays quiet.
          </p>
        </div>
        <div class="ps-preview-row">
          <div class="ps-preview-phone">
            <div class="ps-phone-shell">
              <div class="ps-phone-notch"></div>
              <div class="ps-phone-screen">
                <div class="ps-wa-header">
                  <i class="mdi mdi-whatsapp"></i>
                  <div>
                    <div class="ps-wa-title">Paysol</div>
                    <div class="ps-wa-sub">Business update · 21:00</div>
                  </div>
                </div>
                <div class="ps-wa-msg">
                  <div class="ps-wa-line"><strong>Trend Footwear · Westlands · 06 Aug</strong></div>
                  <div class="ps-wa-line ps-wa-space"></div>
                  <div class="ps-wa-line">Sales: <strong>KES 87,450</strong> · 32 sales</div>
                  <div class="ps-wa-line ps-wa-sub-line">· Cash: KES 34,200 (11)</div>
                  <div class="ps-wa-line ps-wa-sub-line">· M-Pesa: KES 48,750 (18)</div>
                  <div class="ps-wa-line ps-wa-sub-line">· Card: KES 4,500 (3)</div>
                  <div class="ps-wa-line ps-wa-space"></div>
                  <div class="ps-wa-line ps-wa-warn">⚠️ Refunds: 2 (KES 6,800) · JAMES</div>
                  <div class="ps-wa-line ps-wa-warn">⚠️ Discounts: KES 3,200 · MARY</div>
                  <div class="ps-wa-line ps-wa-good">✅ Till close: exact</div>
                  <div class="ps-wa-line ps-wa-space"></div>
                  <div class="ps-wa-line">🔥 Top: Nike AF1-42-BLK (5)</div>
                  <div class="ps-wa-line">📉 Low: Adidas SL-40-WHT (2 left)</div>
                  <div class="ps-wa-line ps-wa-space"></div>
                  <div class="ps-wa-line ps-wa-link">paysol.africa/r/abc123</div>
                </div>
              </div>
            </div>
          </div>
          <div class="ps-preview-copy">
            <h3>Never wonder what happened today.</h3>
            <ul class="ps-check-list">
              <li><i class="mdi mdi-check-circle"></i> Split by payment method, so cash and M-Pesa reconcile at a glance.</li>
              <li><i class="mdi mdi-check-circle"></i> Big refunds and discounts stamped with the cashier's name.</li>
              <li><i class="mdi mdi-check-circle"></i> Till variance flagged — you know instantly if the drawer is short.</li>
              <li><i class="mdi mdi-check-circle"></i> Best-seller + low-stock lines so tomorrow's restock is obvious.</li>
              <li><i class="mdi mdi-check-circle"></i> One tap to the full report if you want the numbers behind the summary.</li>
            </ul>
            <div class="ps-preview-badge">
              <i class="mdi mdi-bell-alert-outline"></i>
              <span>Real-time alerts for the exceptions in-between.</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PRICING -->
    <section id="pricing" class="ps-section ps-section-alt">
      <div class="ps-container">
        <div class="ps-section-head">
          <div class="ps-kicker">Pricing that grows with you</div>
          <h2 class="ps-h2">Start free. Only pay when Paysol pays for itself.</h2>
          <p class="ps-lead">
            30-day free trial on every plan. No card required. Change or cancel anytime.
          </p>
        </div>
        <div class="ps-pricing-grid">
          <div class="ps-price-card">
            <div class="ps-price-head">
              <h3>Starter</h3>
              <p>A single shop finding its feet.</p>
            </div>
            <div class="ps-price-amount">
              <span class="ps-price-currency">KES</span>
              <span class="ps-price-num">0</span>
              <span class="ps-price-per">/ month</span>
            </div>
            <div class="ps-price-hint">First 30 days · Then KES 1,500/mo</div>
            <ul class="ps-price-features">
              <li><i class="mdi mdi-check"></i> 1 shop · 3 users</li>
              <li><i class="mdi mdi-check"></i> Unlimited products &amp; variants</li>
              <li><i class="mdi mdi-check"></i> POS, cash reconciliation, audit log</li>
              <li><i class="mdi mdi-check"></i> M-Pesa STK &amp; C2B</li>
              <li><i class="mdi mdi-check"></i> Daily WhatsApp summary</li>
            </ul>
            <router-link to="/register" class="ps-btn ps-btn-ghost full">Start free</router-link>
          </div>
          <div class="ps-price-card ps-price-popular">
            <div class="ps-price-ribbon">Most popular</div>
            <div class="ps-price-head">
              <h3>Growth</h3>
              <p>Growing business, multiple staff, real reporting.</p>
            </div>
            <div class="ps-price-amount">
              <span class="ps-price-currency">KES</span>
              <span class="ps-price-num">3,900</span>
              <span class="ps-price-per">/ month</span>
            </div>
            <div class="ps-price-hint">30-day free trial included</div>
            <ul class="ps-price-features">
              <li><i class="mdi mdi-check"></i> Up to 3 shops · 15 users</li>
              <li><i class="mdi mdi-check"></i> Purchase orders &amp; landed cost</li>
              <li><i class="mdi mdi-check"></i> Loyalty points &amp; customer wallets</li>
              <li><i class="mdi mdi-check"></i> Layby &amp; installment sales</li>
              <li><i class="mdi mdi-check"></i> Full reports suite + Excel export</li>
              <li><i class="mdi mdi-check"></i> SMS receipts</li>
            </ul>
            <router-link to="/register" class="ps-btn ps-btn-primary full">Start free</router-link>
          </div>
          <div class="ps-price-card">
            <div class="ps-price-head">
              <h3>Enterprise</h3>
              <p>Chains, franchises, restaurants with kitchens.</p>
            </div>
            <div class="ps-price-amount">
              <span class="ps-price-currency">KES</span>
              <span class="ps-price-num">Custom</span>
              <span class="ps-price-per">&nbsp;</span>
            </div>
            <div class="ps-price-hint">Talk to us — sized to your operation</div>
            <ul class="ps-price-features">
              <li><i class="mdi mdi-check"></i> Unlimited shops &amp; users</li>
              <li><i class="mdi mdi-check"></i> Multi-outlet stock transfers</li>
              <li><i class="mdi mdi-check"></i> Restaurant kitchen display + recipes</li>
              <li><i class="mdi mdi-check"></i> Salon appointments &amp; commissions</li>
              <li><i class="mdi mdi-check"></i> Dedicated onboarding &amp; support</li>
            </ul>
            <a href="mailto:hello@paysol.africa" class="ps-btn ps-btn-ghost full">Contact sales</a>
          </div>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="ps-cta-band">
      <div class="ps-container ps-cta-inner">
        <div class="ps-cta-copy">
          <h2>{{ t('landing.cta.title') }}</h2>
          <p>{{ t('landing.cta.sub') }}</p>
        </div>
        <div class="ps-cta-actions">
          <router-link to="/register" class="ps-btn ps-btn-primary ps-btn-lg">
            <i class="mdi mdi-rocket-launch-outline"></i>
            {{ t('landing.cta.primary') }}
          </router-link>
          <router-link to="/login" class="ps-btn ps-btn-ghost ps-btn-lg">
            {{ t('landing.cta.secondary') }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="ps-footer">
      <div class="ps-container ps-footer-grid">
        <div class="ps-footer-brand">
          <img src="/assets/images/paysol_logo.png" alt="Paysol" />
          <p>Business Management Platform. Built for East African operators.</p>
          <div class="ps-footer-social">
            <a href="#"><i class="mdi mdi-twitter"></i></a>
            <a href="#"><i class="mdi mdi-linkedin"></i></a>
            <a href="#"><i class="mdi mdi-instagram"></i></a>
          </div>
        </div>
        <div>
          <h6>Product</h6>
          <a href="#features"  @click.prevent="scrollTo('features')">Features</a>
          <a href="#verticals" @click.prevent="scrollTo('verticals')">Verticals</a>
          <a href="#pricing"   @click.prevent="scrollTo('pricing')">Pricing</a>
          <a href="#how"       @click.prevent="scrollTo('how')">How it works</a>
        </div>
        <div>
          <h6>Company</h6>
          <a href="mailto:hello@paysol.africa">Contact</a>
          <a href="#">About</a>
          <a href="#">Careers</a>
          <a href="#">Blog</a>
        </div>
        <div>
          <h6>Legal</h6>
          <a href="#">Terms</a>
          <a href="#">Privacy</a>
          <a href="#">Data protection</a>
        </div>
      </div>
      <div class="ps-footer-bottom">
        <div class="ps-container ps-footer-bottom-inner">
          <span>© {{ year }} Paysol. All rights reserved.</span>
          <span>Made in Nairobi 🇰🇪</span>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import { t } from '@/i18n';
import LanguageToggle from '@/i18n/LanguageToggle.vue';

export default {
  name: 'LandingPage',
  components: { LanguageToggle },
  data() {
    return {
      localeTick: 0,
      darkMode: (localStorage.getItem('paysol_theme') || 'light') === 'dark',
      scrolled: false,
      mobileOpen: false,
      year: new Date().getFullYear(),
      mockCart: [
        { name: 'Nike Air Force 1',   variant: 'Size 42 · Black', qty: 1, price: 8500 },
        { name: 'Adidas Superstar',   variant: 'Size 40 · White', qty: 2, price: 6200 },
        { name: 'Sports Socks (pair)', variant: 'One size',        qty: 3, price: 350 },
      ],
      verticals: [
        {
          key: 'retail',
          name: 'Footwear & Clothing',
          desc: 'Variants down to the last size × colour. Barcodes, categories, brands, landed cost.',
          icon: 'mdi mdi-shoe-sneaker',
          tint: 'rgba(42,169,171,.12)',
          tags: ['Variants', 'Barcodes', 'Purchase orders', 'Landed cost'],
        },
        {
          key: 'general',
          name: 'General Retail',
          desc: 'Hardware, groceries, electronics, cosmetics. Units, suppliers, cash-heavy operations.',
          icon: 'mdi mdi-store-outline',
          tint: 'rgba(229,165,102,.15)',
          tags: ['Units', 'Suppliers', 'Cash reconciliation', 'Loyalty'],
        },
        {
          key: 'restaurant',
          name: 'Restaurant, Bar & Café',
          desc: 'Recipes deplete stock. Kitchen display, tables, waiters, split bills, happy hour.',
          icon: 'mdi mdi-silverware-fork-knife',
          tint: 'rgba(109,94,189,.15)',
          tags: ['Recipes', 'KDS', 'Tables', 'Tip tracking'],
        },
        {
          key: 'services',
          name: 'Salon, Spa, Clinic',
          desc: 'Appointment book, stylist commissions, online booking, patient records (light).',
          icon: 'mdi mdi-content-cut',
          tint: 'rgba(217,56,74,.12)',
          tags: ['Appointments', 'Commissions', 'Online booking', 'Reminders'],
        },
      ],
      features: [
        { title: 'Products & Variants', icon: 'mdi mdi-package-variant',
          desc: 'One product, many sellable SKUs. Size × colour = own barcode, own count, own price.' },
        { title: 'Fast POS',           icon: 'mdi mdi-cash-register',
          desc: 'Barcode scan, tender, print. Under three seconds per sale, offline-ready.' },
        { title: 'Live Stock',         icon: 'mdi mdi-view-grid-outline',
          desc: 'Per-shop counts, reorder alerts, stock takes with variance reports.' },
        { title: 'Cash Sessions',      icon: 'mdi mdi-cash-clock',
          desc: 'Open, sell, close. Expected vs counted, per shift, per cashier. Shortfalls are visible.' },
        { title: 'Audit Log',          icon: 'mdi mdi-shield-lock-outline',
          desc: 'Every refund, discount, void, price change — stamped with who, what and when.' },
        { title: 'M-Pesa Native',      icon: 'mdi mdi-cellphone-wireless',
          desc: 'STK push, till number, C2B callbacks — no manual code typing at the counter.' },
        { title: 'Remote Dashboard',   icon: 'mdi mdi-monitor-dashboard',
          desc: 'Live sales, top sellers, low-stock alerts, cash expected — from your phone.' },
        { title: 'Customers & Loyalty', icon: 'mdi mdi-account-heart-outline',
          desc: 'Purchase history by phone. Points, tiers, or store credit — your choice.' },
      ],
      patterns: [
        { title: 'Ring &amp; void scam', icon: 'mdi mdi-cash-remove',
          copy: 'Cashier rings a sale, takes the cash, voids it after the customer leaves.',
          caught: 'per-cashier void rate + auto alerts on high-value voids.' },
        { title: 'Friend discount',       icon: 'mdi mdi-tag-off-outline',
          copy: '"Cousin walks in, 50 % off." Discount tools are essential — abuse of them is easy.',
          caught: 'every discount logged by cashier, ceiling enforced, manager PIN for overrides.' },
        { title: 'Till shortage',         icon: 'mdi mdi-scale-balance',
          copy: 'Drawer at close is short of what the system expects. Sometimes error, sometimes not.',
          caught: 'shift-scoped reconciliation. Repeated shortfalls point somewhere specific.' },
        { title: 'No-sale drawer opens',  icon: 'mdi mdi-alert-outline',
          copy: 'Popping the drawer without a sale — usually for change, sometimes to skim.',
          caught: 'every open logged; high frequency per shift is flagged automatically.' },
        { title: 'Ghost stock',           icon: 'mdi mdi-package-variant-closed-remove',
          copy: '"Adjust out damaged item" — but the item goes home in a bag.',
          caught: 'adjustments require reason + manager PIN over threshold; audit trail is complete.' },
        { title: 'Price override',         icon: 'mdi mdi-pencil-off-outline',
          copy: 'Cashier types a lower price at the till, splits the difference with a buyer accomplice.',
          caught: 'price overrides need manager PIN and are always logged with old + new value.' },
      ],
      steps: [
        { icon: 'mdi mdi-account-plus-outline', title: 'Sign up in 60 seconds',
          copy: 'Business name, business type, phone, password. Free 30-day trial — no card, no sales call.' },
        { icon: 'mdi mdi-tag-multiple-outline',  title: 'Add products or import',
          copy: 'Type in a few, or bulk-import a spreadsheet. Variants matter — Paysol handles them from day one.' },
        { icon: 'mdi mdi-cash-register',         title: 'Open your first till',
          copy: 'Declare opening float. Barcode scan, cash or M-Pesa, receipt prints or SMS. That is a sale.' },
        { icon: 'mdi mdi-monitor-eye',           title: 'Watch from anywhere',
          copy: 'Owner dashboard on your phone. Live sales, cash expected, exceptions. Peace of mind.' },
      ],
    };
  },
  computed: {
    mockTotal() { return this.mockCart.reduce((s, it) => s + (it.qty * it.price), 0); },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    this.onScroll();
    this.syncThemeAttribute();
    this._onLocale = () => { this.localeTick++; };
    window.addEventListener('paysol-locale-changed', this._onLocale);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    if (this._onLocale) window.removeEventListener('paysol-locale-changed', this._onLocale);
  },
  methods: {
    t(key, vars) { this.localeTick; return t(key, vars); },
    onScroll() { this.scrolled = window.scrollY > 20; },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); },
    scrollTo(id) {
      if (id === 'top') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.offsetTop - 70, behavior: 'smooth' });
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      const t = this.darkMode ? 'dark' : 'light';
      localStorage.setItem('paysol_theme', t);
      this.syncThemeAttribute();
    },
    syncThemeAttribute() {
      document.documentElement.setAttribute('data-theme', this.darkMode ? 'dark' : 'light');
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   Paysol landing — theme variables via .light-mode / .dark-mode on root.
   Defaults (dark-mode) go on .landing-root, overrides on .light-mode.
   ============================================================================ */
.landing-root {
  --ps-bg1:      #0A1628;
  --ps-bg2:      #0D1B2A;
  --ps-card:    #101F35;
  --ps-line:    #1B2A44;
  --ps-h:       #FFFFFF;
  --ps-body:    #C7D3E8;
  --ps-mute:    #8BA4C4;
  --ps-teal:    #2AA9AB;
  --ps-teal2:   #1F8A8B;
  --ps-orange:  #E5A566;
  --ps-glow:    rgba(42,169,171,.14);
  --ps-nav-bg:  rgba(10,22,40,.72);
  --ps-nav-border: rgba(255,255,255,.06);

  color: var(--ps-body);
  background: var(--ps-bg1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
  overflow-x: hidden;
}
.landing-root.light-mode {
  --ps-bg1:  #FAFBFD;
  --ps-bg2:  #F0F3F8;
  --ps-card: #FFFFFF;
  --ps-line: #E4E9F2;
  --ps-h:    #0d2242;
  --ps-body: #354152;
  --ps-mute: #6B7A8F;
  --ps-teal:    #1F8A8B;
  --ps-teal2:   #147678;
  --ps-glow:    rgba(31,138,139,.10);
  --ps-nav-bg:  rgba(255,255,255,.78);
  --ps-nav-border: rgba(15,34,66,.08);
}

.ps-container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }

/* ---- NAV ---- */
.ps-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 60;
  background: transparent;
  transition: background .25s, backdrop-filter .25s, border-color .25s;
  border-bottom: 1px solid transparent;
}
.ps-nav.scrolled {
  background: var(--ps-nav-bg);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom-color: var(--ps-nav-border);
}
.ps-nav-inner { max-width: 1180px; margin: 0 auto; padding: 12px 24px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.ps-brand { display: inline-flex; align-items: center; cursor: pointer; }
.ps-brand img { height: 36px; width: auto; }
.landing-root:not(.light-mode) .ps-brand img { filter: brightness(1.1); }
.ps-nav-links { display: flex; gap: 6px; }
.ps-nav-links a {
  color: var(--ps-body); font-weight: 500; font-size: 14px;
  padding: 8px 12px; border-radius: 6px; text-decoration: none;
  transition: background .15s, color .15s;
}
.ps-nav-links a:hover { color: var(--ps-teal); background: var(--ps-glow); }
.ps-nav-cta { display: flex; align-items: center; gap: 8px; }
.ps-theme-toggle {
  background: transparent; border: 1px solid var(--ps-line); color: var(--ps-body);
  width: 38px; height: 38px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 18px; transition: all .15s;
}
.ps-theme-toggle:hover { color: var(--ps-teal); border-color: var(--ps-teal); background: var(--ps-glow); }
.ps-nav-burger { display: none; background: transparent; border: none; color: var(--ps-h); cursor: pointer; font-size: 28px; padding: 4px 8px; }
.ps-nav-mobile { display: none; }
@media (max-width: 960px) {
  .ps-nav-links, .ps-nav-cta { display: none; }
  .ps-nav-burger { display: inline-flex; }
  .ps-nav-mobile { display: flex; flex-direction: column; padding: 12px 24px 20px; background: var(--ps-nav-bg); backdrop-filter: blur(14px); border-top: 1px solid var(--ps-nav-border); gap: 4px; }
  .ps-nav-mobile a { color: var(--ps-body); padding: 12px 8px; border-radius: 6px; text-decoration: none; font-weight: 500; }
  .ps-nav-mobile a:hover { background: var(--ps-glow); color: var(--ps-teal); }
  .ps-nav-mobile-cta { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; padding-top: 12px; border-top: 1px solid var(--ps-nav-border); }
}

/* ---- BUTTONS ---- */
.ps-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; border-radius: 8px; font-weight: 600; font-size: 14px;
  text-decoration: none; cursor: pointer; border: none; font-family: inherit;
  transition: transform .05s, background .15s, color .15s, border-color .15s, box-shadow .15s;
}
.ps-btn:active { transform: translateY(1px); }
.ps-btn-lg { padding: 14px 26px; font-size: 15px; }
.ps-btn-primary { background: var(--ps-teal); color: #fff; box-shadow: 0 4px 12px rgba(42,169,171,.25); }
.ps-btn-primary:hover { background: var(--ps-teal2); box-shadow: 0 6px 20px rgba(42,169,171,.35); }
.ps-btn-ghost { background: transparent; color: var(--ps-h); border: 1px solid var(--ps-line); }
.ps-btn-ghost:hover { border-color: var(--ps-teal); color: var(--ps-teal); background: var(--ps-glow); }
.ps-btn.full { width: 100%; justify-content: center; }

/* ---- HERO ---- */
.ps-hero { position: relative; padding: 130px 0 90px; overflow: hidden; }
.ps-hero-glow {
  position: absolute; inset: -20% -10% auto -10%; height: 120%;
  background:
    radial-gradient(ellipse at 20% 20%, var(--ps-glow), transparent 55%),
    radial-gradient(ellipse at 80% 30%, rgba(229,165,102,.10), transparent 55%);
  pointer-events: none;
  z-index: 0;
}
.ps-hero-grid { position: relative; z-index: 1; display: grid; grid-template-columns: 1.15fr 1fr; gap: 60px; align-items: center; }
@media (max-width: 960px) { .ps-hero-grid { grid-template-columns: 1fr; gap: 40px; } .ps-hero { padding: 110px 0 60px; } }

.ps-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--ps-glow); color: var(--ps-teal);
  padding: 7px 14px; border-radius: 999px; font-size: 12px; font-weight: 600;
  border: 1px solid rgba(42,169,171,.28);
}
.ps-badge i { font-size: 15px; }

.ps-h1 {
  color: var(--ps-h); font-size: 54px; line-height: 1.05; font-weight: 800; letter-spacing: -.03em; margin: 18px 0 20px;
}
@media (max-width: 960px) { .ps-h1 { font-size: 40px; } }
@media (max-width: 500px) { .ps-h1 { font-size: 34px; } }
.ps-accent-word {
  background: linear-gradient(90deg, var(--ps-teal), var(--ps-orange));
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
}
.ps-hero-sub { color: var(--ps-body); font-size: 17px; max-width: 560px; line-height: 1.6; margin: 0 0 32px; }
.ps-hero-cta { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 40px; }
.ps-hero-trust {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; max-width: 520px;
}
.ps-trust-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 10px;
}
.ps-trust-item i { color: var(--ps-teal); font-size: 20px; margin-top: 2px; }
.ps-trust-item strong { display: block; color: var(--ps-h); font-size: 13px; }
.ps-trust-item span { color: var(--ps-mute); font-size: 11px; }

/* HERO mockup */
.ps-hero-mockup { position: relative; }
.ps-mock-frame {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 14px;
  box-shadow: 0 30px 60px rgba(10,22,40,.28), 0 0 0 1px rgba(42,169,171,.06);
  overflow: hidden;
}
.landing-root.light-mode .ps-mock-frame { box-shadow: 0 30px 60px rgba(15,34,66,.10), 0 0 0 1px rgba(31,138,139,.06); }
.ps-mock-topbar {
  display: flex; align-items: center; gap: 6px; padding: 10px 14px;
  background: var(--ps-bg2); border-bottom: 1px solid var(--ps-line);
}
.ps-mock-dot { width: 10px; height: 10px; border-radius: 50%; }
.ps-mock-title { color: var(--ps-mute); font-size: 11px; margin-left: 10px; font-weight: 500; }
.ps-mock-body { padding: 16px; }
.ps-mock-cart-row { display: grid; grid-template-columns: 1fr auto auto; gap: 8px; align-items: center; padding: 8px 4px; border-bottom: 1px dashed var(--ps-line); }
.ps-mock-item { color: var(--ps-h); font-weight: 600; font-size: 13px; }
.ps-mock-var { color: var(--ps-mute); font-size: 11px; }
.ps-mock-qty { color: var(--ps-mute); font-size: 12px; font-weight: 600; }
.ps-mock-price { color: var(--ps-h); font-weight: 600; font-size: 13px; }
.ps-mock-total { display: flex; justify-content: space-between; margin: 12px 0 8px; padding-top: 12px; border-top: 1px solid var(--ps-line); }
.ps-mock-total span { color: var(--ps-mute); font-size: 12px; text-transform: uppercase; font-weight: 600; letter-spacing: .5px; }
.ps-mock-total strong { color: var(--ps-teal); font-size: 20px; }
.ps-mock-pay-methods { display: flex; gap: 6px; margin: 10px 0 10px; }
.ps-mock-method { flex: 1; padding: 7px 10px; text-align: center; background: var(--ps-bg2); color: var(--ps-mute); border-radius: 6px; font-size: 11px; font-weight: 600; }
.ps-mock-method.active { background: var(--ps-teal); color: #fff; }
.ps-mock-btn { width: 100%; background: var(--ps-teal); color: #fff; padding: 12px; border: none; border-radius: 8px; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; }
.ps-mock-btn i { font-size: 18px; }
.ps-mock-receipt { display: flex; align-items: center; gap: 6px; margin-top: 10px; color: var(--ps-mute); font-size: 11px; }
.ps-mock-receipt i { color: var(--ps-teal); }
.ps-mock-badge {
  position: absolute; top: -14px; right: -14px;
  background: var(--ps-orange); color: #1a2332;
  padding: 6px 12px; border-radius: 999px; font-size: 11px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 4px;
  box-shadow: 0 8px 20px rgba(229,165,102,.35);
}
.ps-mock-badge i { font-size: 14px; }

/* ---- SECTIONS ---- */
.ps-section { padding: 90px 0; position: relative; }
.ps-section-alt { background: var(--ps-bg2); border-top: 1px solid var(--ps-line); border-bottom: 1px solid var(--ps-line); }
.ps-section-head { text-align: center; max-width: 700px; margin: 0 auto 50px; }
.ps-kicker {
  display: inline-block; color: var(--ps-teal); font-size: 12px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 12px;
}
.ps-h2 { color: var(--ps-h); font-size: 40px; font-weight: 800; letter-spacing: -.02em; margin: 0 0 14px; line-height: 1.15; }
@media (max-width: 700px) { .ps-h2 { font-size: 30px; } }
.ps-lead { color: var(--ps-body); font-size: 16px; line-height: 1.6; margin: 0; }

/* ---- VERTICALS ---- */
.ps-verticals-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 18px; }
.ps-vertical-card {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 12px;
  padding: 24px; transition: border-color .15s, transform .15s, box-shadow .15s;
}
.ps-vertical-card:hover { border-color: var(--ps-teal); transform: translateY(-3px); box-shadow: 0 12px 28px rgba(10,22,40,.18); }
.landing-root.light-mode .ps-vertical-card:hover { box-shadow: 0 12px 28px rgba(15,34,66,.06); }
.ps-vertical-ico { width: 52px; height: 52px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
.ps-vertical-ico i { font-size: 26px; color: var(--ps-teal); }
.ps-vertical-card h4 { color: var(--ps-h); font-size: 17px; margin: 0 0 6px; font-weight: 700; }
.ps-vertical-card p { color: var(--ps-body); font-size: 13px; line-height: 1.55; margin: 0 0 12px; }
.ps-vertical-tags { display: flex; gap: 5px; flex-wrap: wrap; }
.ps-vertical-tags span { color: var(--ps-mute); background: var(--ps-bg2); padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }

/* ---- OVERSIGHT ---- */
.ps-oversight-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin-bottom: 40px; }
.ps-oversight-card {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 12px;
  padding: 20px; position: relative;
}
.ps-oversight-icon { width: 40px; height: 40px; border-radius: 10px; background: var(--ps-glow); display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.ps-oversight-icon i { color: var(--ps-teal); font-size: 20px; }
.ps-oversight-card h5 { color: var(--ps-h); font-size: 14px; margin: 0 0 6px; font-weight: 700; }
.ps-oversight-card p { color: var(--ps-body); font-size: 13px; line-height: 1.5; margin: 0 0 10px; }
.ps-oversight-caught {
  display: flex; align-items: flex-start; gap: 6px; padding-top: 10px; border-top: 1px dashed var(--ps-line);
  color: var(--ps-mute); font-size: 11px; line-height: 1.4;
}
.ps-oversight-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--ps-teal); margin-top: 6px; flex-shrink: 0; }
.ps-oversight-caught em { color: var(--ps-teal); font-style: normal; font-weight: 700; }
.ps-mental-model {
  background: linear-gradient(135deg, var(--ps-bg1), var(--ps-card));
  border: 1px solid var(--ps-line); border-radius: 14px; padding: 32px; text-align: center;
  position: relative; overflow: hidden;
}
.ps-mental-model::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 0%, var(--ps-glow), transparent 60%);
  pointer-events: none;
}
.ps-mental-inner { position: relative; }
.ps-mental-label { color: var(--ps-teal); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; }
.ps-mental-model p { color: var(--ps-h); font-size: 20px; line-height: 1.6; margin: 0; max-width: 640px; margin: 0 auto; font-weight: 500; }
.ps-mental-model p strong { color: var(--ps-teal); }

/* ---- FEATURES ---- */
.ps-features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
.ps-feature-card {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 12px;
  padding: 22px; transition: border-color .15s, transform .15s;
}
.ps-feature-card:hover { border-color: var(--ps-teal); transform: translateY(-3px); }
.ps-feature-icon { width: 44px; height: 44px; border-radius: 10px; background: var(--ps-glow); display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.ps-feature-icon i { color: var(--ps-teal); font-size: 22px; }
.ps-feature-card h4 { color: var(--ps-h); font-size: 15px; margin: 0 0 6px; font-weight: 700; }
.ps-feature-card p { color: var(--ps-body); font-size: 13px; line-height: 1.55; margin: 0; }

/* ---- STEPS ---- */
.ps-steps-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-bottom: 40px; }
.ps-step-card {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 12px;
  padding: 24px; position: relative;
}
.ps-step-num {
  position: absolute; top: -14px; left: 20px;
  background: var(--ps-teal); color: #fff;
  width: 30px; height: 30px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 14px; box-shadow: 0 4px 12px rgba(42,169,171,.35);
}
.ps-step-icon { color: var(--ps-teal); font-size: 32px; margin: 8px 0 10px; }
.ps-step-card h5 { color: var(--ps-h); font-size: 15px; margin: 0 0 6px; font-weight: 700; }
.ps-step-card p { color: var(--ps-body); font-size: 13px; line-height: 1.55; margin: 0; }
.ps-cta-inline { text-align: center; }

/* ---- PREVIEW (WhatsApp mockup) ---- */
.ps-preview-row { display: grid; grid-template-columns: 320px 1fr; gap: 60px; align-items: center; }
@media (max-width: 800px) { .ps-preview-row { grid-template-columns: 1fr; gap: 40px; justify-items: center; } }
.ps-preview-phone { display: flex; justify-content: center; }
.ps-phone-shell {
  width: 280px; background: #111; border-radius: 32px; padding: 12px;
  box-shadow: 0 40px 80px rgba(10,22,40,.35), 0 0 0 3px #222;
  position: relative;
}
.ps-phone-notch {
  position: absolute; top: 20px; left: 50%; transform: translateX(-50%);
  width: 90px; height: 20px; background: #000; border-radius: 12px;
}
.ps-phone-screen { background: #ECE5DD; border-radius: 22px; padding: 40px 12px 12px; min-height: 460px; }
.ps-wa-header { display: flex; align-items: center; gap: 10px; background: #075E54; color: #fff; padding: 8px 12px; border-radius: 10px 10px 0 0; margin: 0 -12px 8px; }
.ps-wa-header i { font-size: 28px; }
.ps-wa-title { font-weight: 600; font-size: 13px; }
.ps-wa-sub { font-size: 10px; color: #b5d4d1; }
.ps-wa-msg { background: #DCF8C6; padding: 12px 12px 10px; border-radius: 10px 10px 10px 2px; box-shadow: 0 1px 2px rgba(0,0,0,.08); font-size: 12px; color: #202124; line-height: 1.5; }
.ps-wa-line { margin: 0; }
.ps-wa-space { height: 6px; }
.ps-wa-sub-line { padding-left: 6px; color: #555; }
.ps-wa-warn { color: #b26200; }
.ps-wa-good { color: #036e2b; }
.ps-wa-link { color: #075E54; font-weight: 600; }
.ps-preview-copy h3 { color: var(--ps-h); font-size: 26px; font-weight: 700; margin: 0 0 20px; letter-spacing: -.01em; }
.ps-check-list { list-style: none; padding: 0; margin: 0 0 24px; }
.ps-check-list li { display: flex; align-items: flex-start; gap: 10px; color: var(--ps-body); font-size: 14px; padding: 6px 0; }
.ps-check-list i { color: var(--ps-teal); font-size: 18px; margin-top: 1px; flex-shrink: 0; }
.ps-preview-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--ps-glow); color: var(--ps-teal); font-weight: 600;
  padding: 10px 16px; border-radius: 8px; font-size: 13px;
}

/* ---- PRICING ---- */
.ps-pricing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 18px; align-items: stretch; }
.ps-price-card {
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 14px;
  padding: 28px; display: flex; flex-direction: column; position: relative;
}
.ps-price-popular { border-color: var(--ps-teal); box-shadow: 0 20px 40px rgba(42,169,171,.15); transform: translateY(-8px); }
.ps-price-ribbon {
  position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
  background: var(--ps-teal); color: #fff;
  padding: 4px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .5px;
}
.ps-price-head { margin-bottom: 20px; }
.ps-price-head h3 { color: var(--ps-h); font-size: 22px; margin: 0 0 4px; font-weight: 800; }
.ps-price-head p { color: var(--ps-mute); font-size: 13px; margin: 0; }
.ps-price-amount { display: flex; align-items: baseline; gap: 4px; margin-bottom: 4px; }
.ps-price-currency { color: var(--ps-mute); font-size: 14px; font-weight: 600; }
.ps-price-num { color: var(--ps-h); font-size: 42px; font-weight: 800; letter-spacing: -.02em; }
.ps-price-per { color: var(--ps-mute); font-size: 13px; }
.ps-price-hint { color: var(--ps-mute); font-size: 11px; margin-bottom: 20px; }
.ps-price-features { list-style: none; padding: 0; margin: 0 0 24px; flex: 1; }
.ps-price-features li { display: flex; align-items: center; gap: 8px; color: var(--ps-body); font-size: 13px; padding: 6px 0; }
.ps-price-features i { color: var(--ps-teal); font-size: 16px; }

/* ---- CTA BAND ---- */
.ps-cta-band {
  padding: 60px 0; margin: 40px 0 0;
  background: linear-gradient(135deg, var(--ps-teal), var(--ps-teal2));
  color: #fff;
}
.ps-cta-inner { display: flex; justify-content: space-between; align-items: center; gap: 30px; flex-wrap: wrap; }
.ps-cta-copy h2 { color: #fff; font-size: 32px; margin: 0 0 4px; font-weight: 800; }
.ps-cta-copy p { color: rgba(255,255,255,.85); font-size: 15px; margin: 0; }
.ps-cta-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.ps-cta-band .ps-btn-primary { background: #fff; color: var(--ps-teal2); box-shadow: 0 6px 20px rgba(0,0,0,.15); }
.ps-cta-band .ps-btn-primary:hover { background: rgba(255,255,255,.9); color: var(--ps-teal2); }
.ps-cta-band .ps-btn-ghost { color: #fff; border-color: rgba(255,255,255,.4); background: transparent; }
.ps-cta-band .ps-btn-ghost:hover { background: rgba(255,255,255,.1); color: #fff; border-color: #fff; }

/* ---- FOOTER ---- */
.ps-footer { background: var(--ps-bg2); border-top: 1px solid var(--ps-line); padding: 60px 0 0; }
.ps-footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 40px; margin-bottom: 40px; }
@media (max-width: 700px) { .ps-footer-grid { grid-template-columns: 1fr 1fr; } .ps-footer-brand { grid-column: 1 / -1; } }
.ps-footer-brand img { height: 40px; margin-bottom: 12px; }
.landing-root:not(.light-mode) .ps-footer-brand img { filter: brightness(1.1); }
.ps-footer-brand p { color: var(--ps-mute); font-size: 13px; line-height: 1.55; margin: 0 0 16px; max-width: 320px; }
.ps-footer-social { display: flex; gap: 8px; }
.ps-footer-social a {
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  background: var(--ps-card); border: 1px solid var(--ps-line); border-radius: 8px;
  color: var(--ps-mute); text-decoration: none; transition: all .15s;
}
.ps-footer-social a:hover { color: var(--ps-teal); border-color: var(--ps-teal); }
.ps-footer h6 { color: var(--ps-h); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 12px; }
.ps-footer a { display: block; color: var(--ps-mute); font-size: 13px; padding: 4px 0; text-decoration: none; }
.ps-footer a:hover { color: var(--ps-teal); }
.ps-footer-bottom { padding: 20px 0; border-top: 1px solid var(--ps-line); }
.ps-footer-bottom-inner { display: flex; justify-content: space-between; color: var(--ps-mute); font-size: 12px; }
</style>
